<template>
  <div
    v-if="!readOnly"
    class="cart-preview__promo-code flex flex-col items-start gap-2"
  >
    <button
      type="button"
      class="inline-flex items-center gap-2 rounded-lg border border-EXPONDO-grey-20 bg-EXPONDO-white
        px-4 py-2 text-sm font-semibold text-EXPONDO-grey hover:border-EXPONDO-grey-60"
      data-testid="toggleCoupon"
      :aria-expanded="isCouponInputOpen"
      @click="isCouponInputOpen = !isCouponInputOpen"
    >
      <span
        class="iconify fa-pro-light--ticket"
        aria-hidden="true"
      />
      <span class="underline">{{ $t('Have a promo code?') }}</span>
      <span
        class="iconify fa-pro-light--chevron-down transition-transform"
        :class="{ 'rotate-180': isCouponInputOpen }"
        aria-hidden="true"
      />
    </button>
    <SectionTransition
      :has-content="isCouponInputOpen"
      class="w-full"
    >
      <div
        class="cart-coupon w-full flex gap-2"
        data-testid="couponForm"
      >
        <PrimaryInput
          v-model="promoCode"
          name="promoCode"
          :label="$t('Enter promo code')"
          :valid="isInputValid"
          :error-message="errorMessage"
          :success-message="successMessage"
          class="cart-preview__promo-code__input flex-1"
          data-testid="cartPromoCode"
          @update:model-value="handleInput"
          @keyup.enter="submitCoupon(promoCode)"
        />
        <div class="w-12">
          <CustomButton
            v-if="!shouldShowRemoveButton"
            type="secondary"
            class="h-12"
            :aria-label="$t('Apply')"
            data-testid="submitCoupon"
            @click="submitCoupon(promoCode)"
          >
            <span class="iconify fa-pro-light--paper-plane-top" />
          </CustomButton>
          <CustomButton
            v-else
            type="secondary"
            class="h-12"
            :aria-label="$t('Remove')"
            data-testid="removeCoupon"
            @click="handleRemoveCoupon(promoCode)"
          >
            <span class="iconify fa-pro-light--trash" />
          </CustomButton>
        </div>
      </div>
    </SectionTransition>
  </div>
</template>

<script lang="ts">

import { CUSTOM_QUERIES } from '~~/constants/customQueries';
import { findDiscountCodeInCart } from '~~/helpers/cart/cartDiscountGetter';
import type { DiscountCodeInfo } from '~~/types/commercetools';
import { DiscountCodeState } from '~~/types/commercetools';

export default defineComponent({
  name: 'CartCoupon',

  props: {
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  setup() {

    const {
      cart,
      applyCoupon,
      removeCoupon,
    } = useCartExtended();

    const COUPON_ERROR = {
      INVALID: 'Invalid promo code',
      COULD_NOT_BE_APPLIED: 'Coupon could not be applied',
      ALREADY_APPLIED: 'This voucher code is already added to your basket.',
    };

    const isCouponInputOpen = ref(false);
    const promoCode = ref('');
    const errorMessage = ref('');
    const successMessage = ref('');

    const isInputValid = computed(() => !errorMessage.value);
    const shouldShowRemoveButton = computed(() =>
      !!successMessage.value || errorMessage.value === COUPON_ERROR.ALREADY_APPLIED,
    );

    const resetState = () => {

      errorMessage.value = '';
      successMessage.value = '';

    };

    const isDiscountActive = (discount: DiscountCodeInfo | undefined): boolean => {

      return discount?.state === DiscountCodeState.MatchesCart;

    };

    const submitCoupon = async (couponCode: string) => {

      resetState();

      if (!couponCode.length) {

        errorMessage.value = COUPON_ERROR.INVALID;

        return;

      }

      const currentDiscount = findDiscountCodeInCart(cart.value, couponCode);

      if (isDiscountActive(currentDiscount)) {

        errorMessage.value = COUPON_ERROR.ALREADY_APPLIED;

        return;

      }

      await applyCoupon({
        couponCode,
        customQuery: CUSTOM_QUERIES.UPDATE_CART_CUSTOM,
      });

      const appliedDiscount = findDiscountCodeInCart(cart.value, couponCode);

      if (isDiscountActive(appliedDiscount)) {

        successMessage.value = 'Discount applied';

      } else if (appliedDiscount) {

        await removeCoupon({
          couponCode,
          customQuery: CUSTOM_QUERIES.UPDATE_CART_CUSTOM,
        });
        errorMessage.value = COUPON_ERROR.COULD_NOT_BE_APPLIED;

      } else {

        errorMessage.value = COUPON_ERROR.INVALID;

      }

    };

    const handleInput = () => {

      resetState();

    };

    const handleRemoveCoupon = async (couponCode: string) => {

      await removeCoupon({
        couponCode,
        customQuery: CUSTOM_QUERIES.UPDATE_CART_CUSTOM,
      });
      promoCode.value = '';
      resetState();

    };

    return {
      isCouponInputOpen,
      promoCode,
      submitCoupon,
      handleRemoveCoupon,
      handleInput,
      errorMessage,
      successMessage,
      shouldShowRemoveButton,
      isInputValid,
    };

  },
});

</script>

<style lang="scss" scoped>

.cart-preview__promo-code__input {
  :deep(input) {
    --input-height: 3rem;
  }
}
</style>
